import { Clipboard } from '@angular/cdk/clipboard';
import { Component, ElementRef, forwardRef, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { firstValueFrom } from 'rxjs';
import { DataSharingToken } from '../../model';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { DataSharingTokenService } from '../../service/data-sharing-token.service';
import { ButtonActionValue, CustomTableColumn, CustomTableService } from '../../shared/custom-table';
import { ListWidgetV2Component } from '../../shared/list-widget-v2/list-widget-v2.components';
import { DataSharingTokenStatusPipe } from '../../shared/pipe/data-sharing-token-status.pipe';
import { ServitlyBadgePipe } from '../../shared/pipe/servitly-badge.pipe';
import { ErrorUtility } from '../../utility/error-utility';
import { SnackbarService } from '../shared/snackbar.service';
import { WidgetPrintResizeCoordinatorService } from '../shared/widget-print-resize-coordinator.service';
import { AcceptDataSharingTokenDialogComponent } from './accept-data-sharing-token-dialog.component';
import { AddDataSharingTokenDialogComponent } from './add-data-sharing-token-dialog.component';

@Component({
    selector: 'data-sharing-token-list-widget',
    template: require('./data-sharing-token-list-widget.component.html'),
    styles: [`custom-table.code-min-width ::ng-deep .mat-column-code  { width: 1% } `]
})
export class DataSharingTokenListWidgetComponent extends ListWidgetV2Component<DataSharingToken> implements OnInit {

    loaded: boolean;
    error: string;
    dataSharingTokens: DataSharingToken[] = [];
    length: number;
    displayedColumns: CustomTableColumn[];
    dataSource = new MatTableDataSource<DataSharingToken>([]);
    isCustomerUser: boolean;
    hasActiveFlatSubscription: boolean;
    hasReadSubscriptionPermission: boolean;

    constructor(
        @Inject(forwardRef(() => DataSharingTokenService)) private dataSharingTokenService: DataSharingTokenService,
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => SnackbarService)) snackbarService: SnackbarService,
        @Inject(forwardRef(() => Clipboard)) private clipboard: Clipboard,
        @Inject(forwardRef(() => ElementRef)) elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) coordinator: WidgetPrintResizeCoordinatorService
    ) { super(appService, authenticationService, null, null, dialog, snackbarService, null, elementRef, coordinator); }

    ngOnInit(): void {
        this.sort = this.sort || ['id', 'asc'];
        this.title = this.title || 'Data Sharing Tokens';
        this.isCustomerUser = this.authenticationService.isCustomerUser();
        this.initDisplayedColumns();
        this.getDataSharingTokenList();
        this.initSubscriptionFlag();
    }

    private initDisplayedColumns(): void {
        this.displayedColumns = [
            CustomTableService.newSimpleColumn('code', 'tokenProperty', 'code').withSortField('code'),
            CustomTableService.newSimpleColumn('thing', 'thingProperty', 'thing.name').withSortField('thing.name'),
            CustomTableService.newDatetimeColumn('creationTimestamp', 'creationDateProperty', 'creationTimestamp', null).withSortField('creationTimestamp'),
        ];
        if (this.isCustomerUser) {
            this.displayedColumns.push(
                CustomTableService.newSimpleColumn('partner', 'dataRecipientProperty', 'partner.name').withSortField('partner.name'),
            );
            if (this.authenticationService.isNewStyleManagerEnabled()) {
                this.displayedColumns.push(CustomTableService.newPipedColumn('status', 'statusProperty', 'status', ServitlyBadgePipe).withArgument({ colorTokenPrefix: 'data-sharing-status' }).withSortField('partnerId'));
            } else {
                this.displayedColumns.push(CustomTableService.newPipedColumn('status', 'statusProperty', 'partnerId', DataSharingTokenStatusPipe).withSortField('partnerId'));
            }
            this.displayedColumns.splice(1, 0, CustomTableService.newButtonColumn('copyToClipboard', '', 'code', null, 'copyToClipboardButton').withMatIcon('content_copy').withMatIconClass('material-symbols-outlined'));
        } else {
            this.displayedColumns.push(CustomTableService.newSimpleColumn('customer', 'customerProperty', 'customer.name').withSortField('customer.name'));
        }

    }

    getDataSharingTokenList(): void {
        this.dataSharingTokenService.getDataSharingTokens(this.pageIndex, this.pageSize, this.sort, this.advancedSearchBody?.key).then(pagedList => {
            this.dataSharingTokens = pagedList.content;
            this.enrichElements(pagedList.content);
            this.updateElementList(pagedList);
            this.error = null;
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }
    private enrichElements(tokens: DataSharingToken[]) {
        tokens.forEach(t => {
            t['status'] = t.partnerId ? 'ACCEPTED' : 'PENDING';
        })
    }

    private initSubscriptionFlag(): void { // only for partners
        if (!this.isCustomerUser) {
            const subscriptionInfo = this.authenticationService.getUser().partner.subscriptionInfo;
            if (subscriptionInfo) {
                this.hasActiveFlatSubscription = !subscriptionInfo.trialActive && !subscriptionInfo.defaultPlan;
            }
        }
    }

    protected refreshList(data?: any): void {
        this.loaded = false;
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.getDataSharingTokenList();
    }

    changePage(pageEvent: PageEvent): void {
        this.pageIndex = pageEvent.pageIndex;
        this.getDataSharingTokenList();
    }

    protected goToDetail(dataSharingToken: DataSharingToken): void {
        // do nothing
    }

    protected subscribeToExportServices(): void {
        // unimplemented method
    }

    addDataSharingToken(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '400px';
        dialogConfig.autoFocus = false;
        firstValueFrom(this.dialog.open(AddDataSharingTokenDialogComponent, dialogConfig).afterClosed()).then(dataSharingToken => {
            if (dataSharingToken) {
                this.refreshList();
            }
        });
    }

    inputDataSharingToken(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.width = '400px';
        dialogConfig.autoFocus = false;
        dialogConfig.data = { hasActiveFlatSubscription: this.hasActiveFlatSubscription };
        firstValueFrom(this.dialog.open(AcceptDataSharingTokenDialogComponent, dialogConfig).afterClosed()).then(dataSharingToken => {
            if (dataSharingToken) {
                this.refreshList();
            }
        });
    }

    onButtonAction(actionValue: ButtonActionValue): void {
        if (actionValue.action == 'copyToClipboard') {
            this.clipboard.copy(actionValue.value);
        }
    }

}